import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import adminApi from '../../services/adminApi';
import NotificationContainer from '../Notification/NotificationContainer';
import './AdminBanners.css';

const AdminBanners = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [editingBanner, setEditingBanner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [banners, setBanners] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    video_url: '',
    link_url: '',
    button_text: 'Explorer',
    position: 'home',
    is_active: true,
    order: 0
  });

  const addNotification = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => removeNotification(id), 5000);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (location.pathname === '/admin/banners/add') {
      if (!loading && banners.length >= 0) {
        setShowAddModal(true);
        navigate('/admin/banners', { replace: true });
      }
    }
  }, [location.pathname, loading, banners.length, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getBanners();
      setBanners(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      addNotification('Erreur lors du chargement des bannières', 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      image_url: '',
      video_url: '',
      link_url: '',
      button_text: 'Explorer',
      position: 'home',
      is_active: true,
      order: 0
    });
    setEditingBanner(null);
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title || '',
      description: banner.description || '',
      image_url: banner.image_url || '',
      video_url: banner.video_url || '',
      link_url: banner.link_url || '',
      button_text: banner.button_text || 'Explorer',
      position: banner.position || 'home',
      is_active: banner.is_active !== undefined ? banner.is_active : true,
      order: banner.order || 0
    });
    setShowAddModal(true);
  };

  const handleDeleteClick = (banner) => {
    setSelectedBanner(banner);
    setShowDeleteModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const bannerData = {
        title: formData.title,
        description: formData.description || null,
        image_url: formData.image_url || null,
        video_url: formData.video_url || null,
        link_url: formData.link_url || null,
        button_text: formData.button_text || 'Explorer',
        position: formData.position || 'home',
        is_active: formData.is_active,
        order: parseInt(formData.order) || 0
      };

      if (editingBanner) {
        await adminApi.updateBanner(editingBanner.id, bannerData);
        addNotification('Bannière modifiée avec succès', 'success');
      } else {
        await adminApi.createBanner(bannerData);
        addNotification('Bannière créée avec succès', 'success');
      }

      setShowAddModal(false);
      resetForm();
      await loadData();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de la sauvegarde';
      addNotification(errorMessage, 'error');
    }
  };

  const handleDelete = async () => {
    if (!selectedBanner) return;

    try {
      await adminApi.deleteBanner(selectedBanner.id);
      addNotification('Bannière supprimée avec succès', 'success');
      setShowDeleteModal(false);
      setSelectedBanner(null);
      await loadData();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de la suppression';
      addNotification(errorMessage, 'error');
    }
  };

  const toggleStatus = async (banner) => {
    try {
      await adminApi.updateBanner(banner.id, {
        ...banner,
        is_active: !banner.is_active
      });
      addNotification(`Bannière ${!banner.is_active ? 'activée' : 'désactivée'}`, 'success');
      await loadData();
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      addNotification('Erreur lors du changement de statut', 'error');
    }
  };

  if (loading) {
    return (
      <div className="admin-banners">
        <div className="loading">Chargement des bannières...</div>
      </div>
    );
  }

  return (
    <div className="admin-banners">
      <div className="admin-header">
        <h1>Gestion des Bannières</h1>
        <button onClick={() => setShowAddModal(true)} className="btn-primary">
          + Ajouter une bannière
        </button>
      </div>

      {banners.length === 0 ? (
        <div className="empty-state">
          <p>Aucune bannière trouvée</p>
          <button onClick={() => setShowAddModal(true)} className="btn-primary">
            Créer la première bannière
          </button>
        </div>
      ) : (
        <div className="banners-grid">
          {banners.map((banner) => (
            <div key={banner.id} className={`banner-card ${!banner.is_active ? 'inactive' : ''}`}>
              <div className="banner-preview">
                {banner.video_url ? (
                  <video src={banner.video_url} muted />
                ) : banner.image_url ? (
                  <img src={banner.image_url} alt={banner.title} />
                ) : (
                  <div className="no-media">Pas de média</div>
                )}
              </div>
              <div className="banner-info">
                <h3>{banner.title}</h3>
                {banner.description && <p>{banner.description}</p>}
                <div className="banner-meta">
                  <span>Position: {banner.position}</span>
                  <span>Ordre: {banner.order}</span>
                  <span className={`status ${banner.is_active ? 'active' : 'inactive'}`}>
                    {banner.is_active ? 'Actif' : 'Inactif'}
                  </span>
                </div>
              </div>
              <div className="banner-actions">
                <button onClick={() => toggleStatus(banner)} className="btn-sm">
                  {banner.is_active ? 'Désactiver' : 'Activer'}
                </button>
                <button onClick={() => handleEdit(banner)} className="btn-sm btn-edit">
                  Modifier
                </button>
                <button onClick={() => handleDeleteClick(banner)} className="btn-sm btn-delete">
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal d'ajout/modification */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => { setShowAddModal(false); resetForm(); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingBanner ? 'Modifier la bannière' : 'Nouvelle bannière'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Titre *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>URL Image</label>
                  <input
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>

                <div className="form-group">
                  <label>URL Vidéo</label>
                  <input
                    type="url"
                    value={formData.video_url}
                    onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div className="form-group">
                <label>URL de redirection</label>
                <input
                  type="url"
                  value={formData.link_url}
                  onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                  placeholder="/products ou https://..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Texte du bouton</label>
                  <input
                    type="text"
                    value={formData.button_text}
                    onChange={(e) => setFormData({ ...formData, button_text: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Position</label>
                  <select
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  >
                    <option value="home">Accueil</option>
                    <option value="category">Catégorie</option>
                    <option value="product">Produit</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Ordre</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                    min="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Bannière active
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => { setShowAddModal(false); resetForm(); }} className="btn-secondary">
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editingBanner ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de suppression */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Supprimer la bannière</h2>
            <p>Êtes-vous sûr de vouloir supprimer la bannière "{selectedBanner?.title}" ?</p>
            <div className="modal-actions">
              <button onClick={() => setShowDeleteModal(false)} className="btn-secondary">
                Annuler
              </button>
              <button onClick={handleDelete} className="btn-danger">
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      <NotificationContainer
        notifications={notifications}
        onRemove={removeNotification}
      />
    </div>
  );
};

export default AdminBanners;

